import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ArrowDownToLine, ArrowUpFromLine, Users, Receipt,
  ShieldCheck, Bell, User, LogOut, Menu, X, Settings, FileClock, UserCog, Coins, Layers, PieChart, Landmark, Rocket, BadgeDollarSign, HeartHandshake,
} from "lucide-react";
import { Logo } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const userNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/plans-portal", label: "Plans", icon: Package },
  { to: "/deposit", label: "Deposit", icon: ArrowDownToLine },
  { to: "/withdrawal", label: "Withdrawal", icon: ArrowUpFromLine },
  { to: "/referrals", label: "Referrals", icon: Users },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/verification", label: "Verification", icon: ShieldCheck },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/profile", label: "Profile", icon: User },
  { to: "/shares-portal", label: "Company Shares", icon: BadgeDollarSign },
  { to: "/my-shares", label: "My Shares", icon: PieChart },
  { to: "/my-share-deposits", label: "My Share Deposits", icon: Landmark },
];

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/users", label: "Users", icon: UserCog },
  { to: "/admin/deposits", label: "Deposits", icon: ArrowDownToLine },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpFromLine },
  { to: "/admin/charity", label: "Charity Fund", icon: HeartHandshake },
  { to: "/admin/investments", label: "Investments", icon: Layers },
  { to: "/admin/referrals", label: "Referrals", icon: Users },
  { to: "/admin/transactions", label: "Transactions", icon: Receipt },
  { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
  { to: "/admin/plans", label: "Plans", icon: Coins },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: FileClock },
  { to: "/admin/shares", label: "Share Management", icon: BadgeDollarSign },
  { to: "/admin/share-deposits", label: "Share Deposits", icon: Landmark },
  { to: "/admin/projects", label: "Upcoming Projects", icon: Rocket },
];

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = async () => {
    try { const res = await api.get("/notifications"); setData(res.data); } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const handler = () => load();
    window.addEventListener("notif-refresh", handler);
    return () => { clearInterval(t); window.removeEventListener("notif-refresh", handler); };
  }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-lg hover:bg-white/5 transition-colors">
          <Bell className="w-5 h-5 text-slate-300" />
          {data.unread > 0 && (
            <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-cyan-400 text-slate-950 text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-[#0A0F1D] border-cyan-500/20 text-slate-200">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {data.unread > 0 && <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-cyan-400 hover:underline">Mark all read</button>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-cyan-500/10" />
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500">No notifications</div>}
          {data.items.slice(0, 12).map((n) => (
            <div key={n.id} className={`px-3 py-2.5 border-b border-white/5 ${!n.read ? "bg-cyan-500/5" : ""}`}>
              <p className="text-sm font-medium text-slate-100">{n.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Shell({ nav, children, brandTo }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const doLogout = () => { logout(); navigate("/login"); };

  const SidebarContent = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-cyan-500/10">
        <Link to={brandTo}><Logo size="sm" noLink /></Link>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((n) => {
          const active = loc.pathname === n.to;
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={`sidebar-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                active ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"}`}>
              <Icon className="w-[18px] h-[18px]" />{n.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-cyan-500/10">
        <button onClick={doLogout} data-testid="sidebar-logout"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/10 transition-colors">
          <LogOut className="w-[18px] h-[18px]" />Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="ait-bg min-h-screen">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 glass border-r border-cyan-500/10 z-40">{SidebarContent}</aside>
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-64 glass z-50">{SidebarContent}</aside>
        </>
      )}
      <div className="lg:pl-64">
        <header className="h-16 glass border-b border-cyan-500/10 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden text-slate-200" data-testid="sidebar-toggle" onClick={() => setOpen(true)}><Menu /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-sm">
                    {user?.full_name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden sm:block text-sm text-slate-200 max-w-[120px] truncate">{user?.full_name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0A0F1D] border-cyan-500/20 text-slate-200 w-48">
                <DropdownMenuLabel className="text-xs text-slate-500 font-mono truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-cyan-500/10" />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} className="cursor-pointer text-red-300">Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}

export function UserLayout({ children }) { return <Shell nav={userNav} brandTo="/dashboard">{children}</Shell>; }
export function AdminLayout({ children }) { return <Shell nav={adminNav} brandTo="/admin">{children}</Shell>; }
